import QueryString from 'qs';

/**
 * Read token (if necessary) Only for serverside usage
 */
const token = process.env.STRAPI_READ_TOKEN;
const domain =
  process.env.NEXT_PUBLIC_STRAPI_ENDPOINT ||
  'https://staging-cms.americastestkitchen.com';

type FetchOptions = {
  noCache?: boolean;
  preview?: boolean;
  revalidate?: number;
  cacheTag?: string;
};

type Params = URLSearchParams | Record<string, unknown>;

export interface StrapiResponse<T> {
  data: T | null;
  error: Record<string, unknown> | null;
}

export async function fetchStrapi<Data>(
  pathname: string,
  parameters: URLSearchParams | Record<string, unknown>,
  options: FetchOptions,
): Promise<StrapiResponse<Data>> {
  const queryString = getQueryParams(parameters, options);
  const nextCacheConfig = setNextCacheOptions(options);
  const response = await fetch(`${domain}${pathname}?${queryString}`, {
    next: nextCacheConfig,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  // default to null so there's no serialization issues in serverside
  const {
    data = null,
    meta = null,
    error = null,
  } = (await response.json()) as any;

  if (error && process.env.NODE_ENV === 'development') {
    // local dev escaped
    // eslint-disable-next-line no-console
    console.log({ data, error, meta });
  }

  return { data, error };
}

const getQueryParams = (parameters: Params, options: FetchOptions) => {
  let queryString: string;

  if (parameters instanceof URLSearchParams) {
    const params = new URLSearchParams(parameters.toString());
    if (options.preview) {
      params.set('publicationState', 'preview');
    }
    queryString = params.toString();
  } else {
    const fullParams = {
      ...parameters,
      ...(options.preview && { publicationState: 'preview' }),
    };
    queryString = QueryString.stringify(fullParams);
  }

  return queryString;
};

const setNextCacheOptions = (options: FetchOptions): NextFetchRequestConfig => {
  let nextCacheConfig: NextFetchRequestConfig = {};

  if (options.noCache || options.preview) {
    nextCacheConfig = { revalidate: 0 };
  } else if (options.revalidate !== undefined) {
    nextCacheConfig = { revalidate: options.revalidate };
  }

  if (options.cacheTag) {
    nextCacheConfig.tags = [options.cacheTag];
  }

  return nextCacheConfig;
};
